import { captureException } from '@sentry/react';
import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, where } from '@firebase/firestore';

import type { StatusActivity, StatusActivityAction } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';
import { getStepConfig } from '../getStepConfig';
import type { TimelineItem, HistoryDialogFilters } from './HistoryDialog.types.ts';

type BuildQueryParams = {
  filters?: HistoryDialogFilters;
};

const buildQuery = ({ filters }: BuildQueryParams) => {
  if (!filters) {
    return [orderBy('actionedAt', 'desc')];
  }

  const query = [];

  if (filters.activeStep?.id) {
    query.push(where('stepId', '==', filters.activeStep.id));
  }

  if (filters.sortOrder) {
    query.push(orderBy('actionedAt', filters.sortOrder));
  }

  return query;
};

type UseIntakeStatusActivityParams = {
  patientId: string;
  enabled?: boolean;
  filters?: HistoryDialogFilters;
};

export const useIntakeStatusActivity = ({ patientId, enabled = true, filters }: UseIntakeStatusActivityParams) => {
  const [allActivities, setAllActivities] = useState<StatusActivity<StatusActivityAction>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !patientId) {
      setAllActivities([]);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);

    const activityQuery = query(
      collection(db, 'patients', patientId, 'intakeStatusActivity'),
      ...buildQuery({ filters }),
      // FUTURE: add pagination
    );

    const unsubscribe = onSnapshot(
      activityQuery,
      (snapshot) => {
        try {
          const activities = snapshot.docs.map((doc: any) => ({
            ...doc.data(),
            id: doc.id,
          })) as StatusActivity<StatusActivityAction>[];

          setAllActivities(activities);
          setIsLoading(false);
          setError(null);
        } catch (Error) {
          if (import.meta.env.DEV) {
            console.error('Error fetching intake status activities:', { error, activityQuery });
          }

          captureException(error, {
            tags: { issueIn: 'useAllIntakeStatusActivity' },
            extra: { patientId },
          });

          setError(error);
          setIsLoading(false);
        }
      },
      (err) => {
        if (import.meta.env.DEV) {
          console.error('Error fetching intake status activities:', err);
        }

        captureException(err, {
          tags: { issueIn: 'useAllIntakeStatusActivity' },
          extra: { patientId },
        });

        setError(err);
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, patientId, filters]);

  // Transform activities into timeline items
  const timelineItems = useMemo(() => {
    if (!allActivities.length) {
      return [];
    }

    return allActivities.map((activity) => {
      const stepSnapshot = activity.stepSnapshot;
      const stepType = stepSnapshot.stepType;

      if (stepType === 'BASE_STEP') {
        const stepConfig = getStepConfig(stepSnapshot.id);
        const description = stepConfig.statuses[stepSnapshot.currentStatus?.status || 'NOT_STARTED'].description({
          step: stepSnapshot,
          isHealthcareProvider: true,
        });

        return {
          id: (activity as any).id,
          title: stepSnapshot.id.replace(/_/g, ' '),
          description,
          ...activity,
        } satisfies TimelineItem;
      }

      return {
        id: (activity as any).id,
        title: stepSnapshot.title,
        description: stepSnapshot.description,
        ...activity,
      } satisfies TimelineItem;
    });
  }, [allActivities, filters]);

  return {
    activities: allActivities,
    timelineItems,
    isLoading,
    error,
  } as const;
};
